import {
  Alert,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  ImageList,
  ImageListItem,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  DriverVehicleInformationVehicleTypes,
  getVehicleClassForVehicleType,
  getVehicleTypesForVehicleClass,
  isAnyKeyValueTrue,
  ServiceDriverTypes,
  validatePresence,
} from "../../helpers/validationHelpers";
import { range } from "../../helpers/formatters";
import useFormFieldsBlurState from "../../hooks/useFormFieldsBlurState";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRecoilState } from "recoil";
import { userAuthState } from "../../atom";
import { useOrganizationPublicFacingBillingSettings } from "../../hooks/useOrganizations";
import {
  convertDriverUsingOwnVehicleOptionToBoolean,
  DriverUsingOwnVehicleOptions,
} from "./constants";

const DriverVehicleDetailsForm = ({
  driverUsingOwnVehicle,
  isRootOrg,
  vehicleClassMappingsData,
  vehicleType,
  setVehicleType,
  makeOfCar,
  setMakeOfCar,
  licensePlateNumber,
  setLicensePlateNumber,
  carColor,
  setCarColor,
  carYear,
  setCarYear,
  selectedEquipment,
  setSelectedEquipment,
  otherEquipmentDetails,
  setOtherEquipmentDetails,
}: {
  driverUsingOwnVehicle: boolean;
  isRootOrg?: boolean;
  vehicleClassMappingsData: any[];
  vehicleType: DriverVehicleInformationVehicleTypes;
  setVehicleType: any;
  makeOfCar: string;
  setMakeOfCar: Dispatch<SetStateAction<string>>;
  licensePlateNumber: string;
  setLicensePlateNumber: Dispatch<SetStateAction<string>>;
  carColor: string;
  setCarColor: Dispatch<SetStateAction<string>>;
  carYear: any;
  setCarYear: Dispatch<SetStateAction<any>>;
  selectedEquipment: any;
  setSelectedEquipment: Dispatch<SetStateAction<any>>;
  otherEquipmentDetails: string;
  setOtherEquipmentDetails: Dispatch<SetStateAction<string>>;
}) => {
  const [authDetails, setAuthDetails] = useRecoilState(userAuthState);
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();

  const yearRange = range(1960, currentYear).reverse();

  const { blurredFormFields, setFormFieldToBlurred, setFormFieldToFocused } =
    useFormFieldsBlurState({
      vehicleType,
      licensePlateNumber,
      makeOfCar,
      carColor,
      carYear,
    });

  const formErrors = {
    licensePlateNumber: {
      error:
        blurredFormFields.licensePlateNumber &&
        !validatePresence(licensePlateNumber) &&
        "Please enter your license plate number",
    },
    makeOfCar: {
      error:
        blurredFormFields.makeOfCar &&
        !validatePresence(makeOfCar) &&
        "Please enter the make of your car",
    },
    carColor: {
      error:
        blurredFormFields.carColor &&
        !validatePresence(carColor) &&
        "Please enter the car's color",
    },
    otherEquipmentDetails: {
      error:
        selectedEquipment["Other"] &&
        !validatePresence(otherEquipmentDetails) &&
        "Please provide more details on what other equipment you're using",
    },
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === "None" && event.target.checked) {
      // Uncheck everything else
      setSelectedEquipment({
        "Ratchet Straps": false,
        "Moving Blankets": false,
        Tarp: false,
        Dolly: false,
        Trailer: false,
        Other: false,
        None: event.target.checked,
      });
      // Reset other equipment text field
      setOtherEquipmentDetails("");
    } else {
      setSelectedEquipment((prev: any) => ({
        ...prev,
        [event.target.name]: event.target.checked,
      }));
    }
  };

  return (
    <>
      <Grid container item spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="vehicle-type-select-label">Vehicle Type</InputLabel>
            {isRootOrg ? (
              <Select
                value={vehicleType}
                label="Vehicle Type"
                onChange={(e) => {
                  setVehicleType(e.target.value);
                }}
                onFocus={() => {
                  setFormFieldToFocused("vehicleType");
                }}
                onBlur={() => {
                  setFormFieldToBlurred("vehicleType");
                }}
              >
                <MenuItem
                  value={DriverVehicleInformationVehicleTypes.pickUpTruck}
                >
                  Pick Up Truck
                </MenuItem>
                <MenuItem value={DriverVehicleInformationVehicleTypes.cargoVan}>
                  Cargo Van
                </MenuItem>
                <MenuItem value={DriverVehicleInformationVehicleTypes.minivan}>
                  Minivan
                </MenuItem>
                <MenuItem value={DriverVehicleInformationVehicleTypes.suv}>
                  SUV
                </MenuItem>
                <MenuItem value={DriverVehicleInformationVehicleTypes.sedan}>
                  Sedan
                </MenuItem>
              </Select>
            ) : (
              <Select
                value={vehicleType}
                label="Vehicle Type"
                onChange={(e) => {
                  setVehicleType(e.target.value);
                }}
                onFocus={() => {
                  setFormFieldToFocused("vehicleType");
                }}
                onBlur={() => {
                  setFormFieldToBlurred("vehicleType");
                }}
              >
                {vehicleClassMappingsData?.length &&
                  vehicleClassMappingsData?.map((vehicleClassMapping) => {
                    // Gets the first available vehicle type for this vehicle mapping
                    // Large = Pickup Truck
                    // Medium = Minivan
                    // Small = Sedan
                    const vehicleTypesForCurrentClass =
                      getVehicleTypesForVehicleClass(
                        vehicleClassMapping?.vehicleClass
                      );

                    if (vehicleTypesForCurrentClass?.length) {
                      return (
                        <MenuItem
                          key={`vehicle-details-vehicle-class-mapping-${vehicleClassMapping?.id}`}
                          value={vehicleTypesForCurrentClass[0]}
                        >
                          {vehicleClassMapping?.name}
                        </MenuItem>
                      );
                    }
                  })}
              </Select>
            )}
          </FormControl>
        </Grid>
      </Grid>
      {driverUsingOwnVehicle && (
        <>
          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={"License Plate #"}
                fullWidth
                value={licensePlateNumber}
                error={Boolean(
                  isAnyKeyValueTrue(formErrors.licensePlateNumber)
                )}
                helperText={isAnyKeyValueTrue(formErrors.licensePlateNumber)}
                onChange={(e) => {
                  setLicensePlateNumber(e.target.value);
                }}
                onFocus={() => {
                  setFormFieldToFocused("licensePlateNumber");
                }}
                onBlur={() => {
                  setFormFieldToBlurred("licensePlateNumber");
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={"Make of Car"}
                fullWidth
                value={makeOfCar}
                error={Boolean(isAnyKeyValueTrue(formErrors.makeOfCar))}
                helperText={isAnyKeyValueTrue(formErrors.makeOfCar)}
                onChange={(e) => {
                  setMakeOfCar(e.target.value);
                }}
                onFocus={() => {
                  setFormFieldToFocused("makeOfCar");
                }}
                onBlur={() => {
                  setFormFieldToBlurred("makeOfCar");
                }}
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={"Car Color"}
                fullWidth
                value={carColor}
                error={Boolean(isAnyKeyValueTrue(formErrors.carColor))}
                helperText={isAnyKeyValueTrue(formErrors.carColor)}
                onChange={(e) => {
                  setCarColor(e.target.value);
                }}
                onFocus={() => {
                  setFormFieldToFocused("carColor");
                }}
                onBlur={() => {
                  setFormFieldToBlurred("carColor");
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Year of Make</InputLabel>
                <Select
                  value={carYear}
                  label="Year of Make"
                  onChange={(e) => {
                    setCarYear(e.target.value);
                  }}
                  onFocus={() => {
                    setFormFieldToFocused("carYear");
                  }}
                  onBlur={() => {
                    setFormFieldToBlurred("carYear");
                  }}
                >
                  {yearRange.map((year) => (
                    <MenuItem value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item>
              <FormControl
                sx={{ m: 3 }}
                component="fieldset"
                variant="standard"
              >
                <FormLabel component="legend">
                  Which of the following tools/equipment do you have?
                </FormLabel>
                <FormGroup>
                  <FormControlLabel
                    disabled={selectedEquipment["None"]}
                    control={
                      <Checkbox
                        checked={selectedEquipment["Ratchet Straps"]}
                        onChange={handleChange}
                        name="Ratchet Straps"
                      />
                    }
                    label="Ratchet Straps"
                  />
                  <FormControlLabel
                    disabled={selectedEquipment["None"]}
                    control={
                      <Checkbox
                        checked={selectedEquipment["Moving Blankets"]}
                        onChange={handleChange}
                        name="Moving Blankets"
                      />
                    }
                    label="Moving Blankets"
                  />
                  <FormControlLabel
                    disabled={selectedEquipment["None"]}
                    control={
                      <Checkbox
                        checked={selectedEquipment["Tarp"]}
                        onChange={handleChange}
                        name="Tarp"
                      />
                    }
                    label="Tarp"
                  />
                  <FormControlLabel
                    disabled={selectedEquipment["None"]}
                    control={
                      <Checkbox
                        checked={selectedEquipment["Trailer"]}
                        onChange={handleChange}
                        name="Trailer"
                      />
                    }
                    label="Trailer"
                  />
                  <FormControlLabel
                    disabled={selectedEquipment["None"]}
                    control={
                      <Checkbox
                        checked={selectedEquipment["Other"]}
                        onChange={handleChange}
                        name="Other"
                      />
                    }
                    label="Other"
                  />
                  {selectedEquipment["Other"] && (
                    <TextField
                      disabled={selectedEquipment["None"]}
                      sx={{ mt: 2 }}
                      label={"Other equipment"}
                      fullWidth
                      value={otherEquipmentDetails}
                      error={Boolean(
                        isAnyKeyValueTrue(formErrors.otherEquipmentDetails)
                      )}
                      helperText={isAnyKeyValueTrue(
                        formErrors.otherEquipmentDetails
                      )}
                      onChange={(e) => {
                        setOtherEquipmentDetails(e.target.value);
                      }}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedEquipment["None"]}
                        onChange={handleChange}
                        name="None"
                      />
                    }
                    label="None"
                  />
                </FormGroup>
              </FormControl>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default DriverVehicleDetailsForm;
